import merchantOpenApiService from "../../services/api/merchantOpenApiService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { merchantQueryKeys } from "./useMerchantQueries";
import { QUERY_CONFIGS } from "../../lib/react-query/config";

export const merchantOpenApiQueryKeys = {
  all: ["merchant-open-apis"],
  enableApi: () => [...merchantOpenApiQueryKeys.all, "enable-api"],
  disableApi: () => [...merchantOpenApiQueryKeys.all, "disable-api"],
  oauthClient: () => [...merchantOpenApiQueryKeys.all, "oauth-client"],
  createOAuthClient: () => [...merchantOpenApiQueryKeys.all, "create-oauth-client"],
  rotateOAuthClient: () => [...merchantOpenApiQueryKeys.all, "rotate-oauth-client"],
  revokeOAuthClient: () => [...merchantOpenApiQueryKeys.all, "revoke-oauth-client"],
  callbackUrl: () => [...merchantOpenApiQueryKeys.all, "callback-url"],
  saveCallbackUrl: () => [...merchantOpenApiQueryKeys.all, "save-callback-url"],
};

export const useEnableApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => merchantOpenApiService.enableApi(),
    mutationKey: merchantOpenApiQueryKeys.enableApi(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.profile(),
      });
    },
  });
};

export const useDisableApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => merchantOpenApiService.disableApi(),
    mutationKey: merchantOpenApiQueryKeys.disableApi(),
    onSettled: async () => {
      await queryClient.refetchQueries({
        queryKey: merchantQueryKeys.profile(),
      });
      if (queryClient.getQueryData(merchantOpenApiQueryKeys.oauthClient())) {
        queryClient.removeQueries({
          queryKey: merchantOpenApiQueryKeys.oauthClient(),
        });
      }
      if (queryClient.getQueryData(merchantOpenApiQueryKeys.callbackUrl())) {
        queryClient.removeQueries({
          queryKey: merchantOpenApiQueryKeys.callbackUrl(),
        });
      }
    },
  });
};

export const useGetOAuthClient = (apiEnabled) => {
  return useQuery({
    queryKey: merchantOpenApiQueryKeys.oauthClient(),
    queryFn: merchantOpenApiService.getOAuthClient,
    enabled: !!apiEnabled,
    ...QUERY_CONFIGS.STATIC,
    retry: false,
  });
};

export const useCreateOAuthClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => merchantOpenApiService.createOAuthClient(data),
    mutationKey: merchantOpenApiQueryKeys.createOAuthClient(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantOpenApiQueryKeys.oauthClient(),
      });
    },
  });
};

export const useRotateOAuthClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (publicId) => merchantOpenApiService.rotateOAuthClient(publicId),
    mutationKey: merchantOpenApiQueryKeys.rotateOAuthClient(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantOpenApiQueryKeys.oauthClient(),
      });
    },
  });
};

export const useRevokeOAuthClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (publicId) => merchantOpenApiService.revokeOAuthClient(publicId),
    mutationKey: merchantOpenApiQueryKeys.revokeOAuthClient(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantOpenApiQueryKeys.oauthClient(),
      });
    },
  });
};

export const useGetCallbackUrl = (apiEnabled) => {
  return useQuery({
    queryKey: merchantOpenApiQueryKeys.callbackUrl(),
    queryFn: merchantOpenApiService.getCallbackUrl,
    enabled: !!apiEnabled,
    ...QUERY_CONFIGS.STATIC,
    retry: false,
  });
};

export const useSaveCallbackUrl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => merchantOpenApiService.saveCallbackUrl(data),
    mutationKey: merchantOpenApiQueryKeys.saveCallbackUrl(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantOpenApiQueryKeys.callbackUrl(),
      });
    },
  });
};

